import { useMemo } from "react";

import type { PreMeetingPrepGoalItem } from "../types";

import { useSearchService } from "../../../../api/hooks";

import { SERVICE_GOAL } from "../../../../api/services/defs/search-service";
import type { Goal } from "../../../../api/services/searchServiceResultTypes/goal";

type UsePreMeetingPrepGoalsResult = {
	goals: PreMeetingPrepGoalItem[];
	overdue: PreMeetingPrepGoalItem[];
	upcomingDueDate: PreMeetingPrepGoalItem[];
	outstandingUpdates: PreMeetingPrepGoalItem[];
	isLoading: boolean;
};

export const usePreMeetingPrepGoals = ({
	directReportId,
	enabled = true,
}: {
	directReportId?: string;
	enabled?: boolean;
}): UsePreMeetingPrepGoalsResult => {
	const { data: goalsData, isLoading } = useSearchService<Goal>({
		service: SERVICE_GOAL,
		filters: directReportId
			? {
					assignee_in: [directReportId],
					status_in: ["active"],
				}
			: {},
		limit: 50,
		disabled: !enabled || !directReportId,
	});

	const goals = useMemo(() => {
		if (!goalsData?.results) return [];

		const now = new Date();

		return goalsData.results.map((goal): PreMeetingPrepGoalItem => {
			const dueDate = goal.endDate ? new Date(goal.endDate) : null;
			const isOverdue = dueDate ? dueDate < now : false;
			const hasUpcomingDueDate = dueDate ? dueDate >= now : false;
			const hasOutstandingUpdates = goal.hasUpdates === false;

			return {
				goalId: goal._id,
				goalName: goal.name,
				status: goal.status,
				dueDate: goal.endDate || "",
				lastUpdate: goal.createdAt || "",
				isOverdue,
				hasUpcomingDueDate,
				hasOutstandingUpdates,
				progress: goal.progress ?? 0,
				assigneeName: goal.assignee?.name || "",
			};
		});
	}, [goalsData]);

	const categorized = useMemo(() => {
		const overdue = goals.filter((g) => g.isOverdue);
		const upcomingDueDate = goals.filter((g) => g.hasUpcomingDueDate);
		const outstandingUpdates = goals.filter(
			(g) => g.hasOutstandingUpdates && !g.isOverdue && !g.hasUpcomingDueDate
		);

		return { overdue, upcomingDueDate, outstandingUpdates };
	}, [goals]);

	return {
		goals,
		...categorized,
		isLoading,
	};
};
